import { ModalComponent } from '../../../components/ModalComponent/ModalComponent';
import { Deal, LoadingId } from '../../../types';

import { styled, useTheme } from '@mui/material';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info-blue.svg';
import { Typography } from '../../../components/Typography/Typography';
import { useLoadingBar } from '../../../hooks/useLoadingBar';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { extractErrorMessage } from '../../../utils/errorNotifier';
import { useArchiveDeal } from '../../../queries/hooks/useArchiveDeal';

type Props = {
  archiveDealOpen: boolean;
  toggleArchiveDeal: () => void;
  deal: Deal;
};

export function ArchiveDealModal({ deal, archiveDealOpen, toggleArchiveDeal }: Props) {
  const { colors } = useTheme();
  const { pushSuccessToast, pushErrorToast } = useToastMessage();
  const { startLoading, stopLoading } = useLoadingBar();

  const dealName = deal?.name;
  const { mutate: onArchiveDeal, isLoading: archiveInProgress } = useArchiveDeal();

  const handleArchiveDeal = async () => {
    startLoading(LoadingId.deleteDeal);
    onArchiveDeal(deal.id, {
      onSuccess: () => {
        pushSuccessToast({
          title: 'Deal is Archived',
          message: `Deal ${dealName} has been successfully archived.`,
        });
        toggleArchiveDeal();
      },
      onError: (error: unknown) => {
        pushErrorToast({ message: extractErrorMessage(error) });
      },
      onSettled: () => {
        stopLoading(LoadingId.deleteDeal);
      },
    });
  };

  return (
    <ModalComponent
      isOpen={archiveDealOpen}
      onClose={toggleArchiveDeal}
      actionButtonsProps={{
        isSubmitDisabled: archiveInProgress,
        onSubmit: handleArchiveDeal,
        onCancel: toggleArchiveDeal,
        submitText: 'Archive Deal',
        submitBtnStyle: {
          background: archiveInProgress ? undefined : colors.iconStatus.error,
        },
      }}
      title={`Archive ${dealName} deal`}
      top='80px'
    >
      <InfoWrapper>
        <InfoIcon />
        <Typography variant='body' color={colors.textAccent.default}>
          {`Are you sure you want to archive deal ${dealName}? It will be removed from active deals and future timesheets, but kept in the system and in existing timesheets.`}
        </Typography>
      </InfoWrapper>
    </ModalComponent>
  );
}

const InfoWrapper = styled('div')`
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.surfaceBackground.highlighted};
  border-radius: 2px;
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
`;
